import { apiUrl } from '../config/constants';

export type GoogleClickIds = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
};

const CLICK_ID_FORMAT = /^[A-Za-z0-9_\-.]{8,200}$/;

// _gcl_* cookie values look like GCL.<timestamp>.<clickId>; the click id is everything after the second dot
const parseGclCookie = (value: string): string | undefined => {
  const parts = value.split('.');
  if (parts.length < 3) return undefined;
  const clickId = parts.slice(2).join('.');
  return CLICK_ID_FORMAT.test(clickId) ? clickId : undefined;
};

const readCookie = (name: string): string | undefined => {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.substring(name.length + 1)) : undefined;
};

const readQueryParam = (name: string): string | undefined => {
  const value = new URLSearchParams(window.location.search).get(name);
  return value && CLICK_ID_FORMAT.test(value) ? value : undefined;
};

export const readGoogleClickIds = (): GoogleClickIds | null => {
  // The _gcl_* cookies only exist when the paypage is served on the brand's own domain
  // (shared eTLD+1 with the site the ad landed on); everywhere else this returns null
  const awCookie = readCookie('_gcl_aw');
  const gbCookie = readCookie('_gcl_gb');

  const gclid = (awCookie && parseGclCookie(awCookie)) || readQueryParam('gclid');
  const gbraid = (gbCookie && parseGclCookie(gbCookie)) || readQueryParam('gbraid');
  const wbraid = readQueryParam('wbraid');

  if (!gclid && !gbraid && !wbraid) {
    return null;
  }
  return { gclid, gbraid, wbraid };
};

// Booking pages (/{guid}, /t/{guid}) carry a booking guid; payment links (/pp/{guid}) a payment guid
export type ClickIdTarget = 'booking' | 'payment';

const captureUrl = (guid: string, target: ClickIdTarget) =>
  target === 'payment' ? `${apiUrl}/payments/${guid}/clickids` : `${apiUrl}/bookings/payment/${guid}/clickids`;

const sentKey = (guid: string) => `td_gcl_sent_${guid}`;
const inFlight = new Set<string>();

// Fire-and-forget: must never affect the payment flow, so every failure is swallowed.
// Deliberately bypasses fetchRequest — its error handling toasts to the customer.
export const captureClickIds = (guid: string, target: ClickIdTarget = 'booking'): void => {
  try {
    const clickIds = readGoogleClickIds();
    if (!clickIds) {
      return;
    }
    if (inFlight.has(guid) || sessionStorage.getItem(sentKey(guid))) {
      return;
    }
    inFlight.add(guid);
    window
      .fetch(captureUrl(guid, target), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clickIds),
        // survives an immediate navigation away (e.g. Tap 3-DS redirect)
        keepalive: true,
      })
      .then((response) => {
        if (response.ok) {
          sessionStorage.setItem(sentKey(guid), '1');
        }
      })
      .catch(() => undefined)
      .finally(() => inFlight.delete(guid));
  } catch {
    // cookie/sessionStorage access can throw in strict privacy modes; capture is best-effort
  }
};
